/**
 * useActiveChannels — resolve the Delivery Channel Master for a center.
 *
 * Returns { channels, loading }. `channels` is the list resolved from
 * `POST /api/delivery-channels/center/{code}`. Falls back to [] on
 * error or when center is missing.
 *
 * Also exports `readChannelValue(doc, channel, prefix)` — the single
 * place that knows about legacy flat columns vs. nested delivery_extra.
 * Every screen that renders per-channel sale figures MUST use this
 * helper (see DailyTextGenerator tabs).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const LEGACY_KEYS = new Set(["swiggy", "zomato", "doordash", "ubereats"]);

// Module-level cache: many screens (4-tab Sales Text Generator, etc.)
// switch tabs and remount consumers. Cache resolved channels keyed by
// `${token}|${center}` to avoid redundant round-trips. Invalidated when
// the admin saves an override — do a hard refresh of the app after
// changing the master to see the update everywhere.
const _channelCache = new Map();

export function useActiveChannels(session, center) {
  const [channels, setChannels] = useState(() => {
    const cacheKey = `${session?.token || ""}|${center || ""}`;
    return _channelCache.get(cacheKey) || [];
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!session?.token || !center) {
      setChannels([]);
      return;
    }
    const cacheKey = `${session.token}|${center}`;
    const cached = _channelCache.get(cacheKey);
    if (cached) {
      setChannels(cached);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await api.post(`/delivery-channels/center/${center}`, {
          token: session.token,
        });
        const ch = res.data.channels || [];
        _channelCache.set(cacheKey, ch);
        if (!cancelled) setChannels(ch);
      } catch {
        if (!cancelled) setChannels([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.token, center]);

  return { channels, loading };
}

/**
 * Read a channel's sale amount from a summary document.
 *
 * @param doc     the daily / weekly / monthly / yearly summary object
 * @param channel { key, label, field } from the master
 * @param prefix  "" for daily (fields like `swiggy`), "total_" for
 *                weekly / monthly / yearly aggregates (`total_swiggy`)
 */
export function readChannelValue(doc, channel, prefix = "") {
  if (!doc || !channel) return 0;
  const key = channel.key;
  // Try flat key first (both legacy `swiggy` and flattened custom `grubhub`).
  const flatV = doc[`${prefix}${key}`];
  if (flatV != null) return Number(flatV) || 0;
  // Fall back to nested delivery_extra.
  const bucket = prefix
    ? (doc[`${prefix}delivery_extra`] || doc.total_delivery_extra)
    : doc.delivery_extra;
  return Number((bucket || {})[key] || 0);
}

/** Frontend key used inside editableData / composed text. Flat, never nested. */
export function channelDocKey(channel, _prefix = "") {
  return channel.key;
}

/**
 * Flatten `delivery_extra.<key>` (and `total_delivery_extra.<key>`) values
 * onto the top-level doc so the rest of the codebase can read them as
 * flat keys like `grubhub` or `total_grubhub`.
 *
 * Non-destructive — returns a new object.
 */
export function flattenChannels(doc, prefix = "") {
  if (!doc) return doc;
  const nested = prefix
    ? (doc[`${prefix}delivery_extra`] || doc.total_delivery_extra)
    : doc.delivery_extra;
  if (!nested || typeof nested !== "object") return doc;
  const out = { ...doc };
  for (const [k, v] of Object.entries(nested)) {
    const flatKey = `${prefix}${k}`;
    if (out[flatKey] == null) out[flatKey] = v;
  }
  return out;
}
